import React, { useContext, useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AppContext } from '@state/context'
import { openPanelWithData } from '@state/actions/panelsActions'
import {
  PANELS,
  BUTTON_SIZE,
  BUTTON_TYPE,
  CONTAINER_STYLE,
  HEADING_TYPE,
  TEXT_SIZES,
  DIVIDER_SIZE,
  COLORS,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import SubHeader from '@views/SubHeader'
import Container from '@components/Container'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Text from '@components/Text'
import Loader from '@components/Loader'
import Button from '@components/Button'
import SelectButton from '@components/SelectButton'
import GridColumns from '@components/GridColumns'
import StudentList from '@components/StudentList'
import SharedBooksList from '@components/SharedBooksList'
import { useUpdateDemoSchoolGroup } from '@hooks/mutations/useUpdateDemoSchoolGroup'
import { useDemoResources } from '@hooks/useDemoResources'
import ImageGallery from '@components/ImageGallery'
import { useSchoolGroupsOptions } from '@hooks/useSchoolGroupsOptions'
import { storagePath } from '@helpers/variables'

const SingleDemoSchoolGroup = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [schoolGroup, setSchoolGroup] = useState()
  const [showGallery, setShowGallery] = useState()
  const breadcrumb = useMemo(() => (schoolGroup && {
    label: schoolGroup?.name ?? '',
  }), [schoolGroup])

  const [, dispatch] = useContext(AppContext)
  const {
    demoSchoolGroups,
    error,
    isLoading: demoSchoolClassesLoading,
  } = useDemoResources()
  const { schoolGroupsOptions } = useSchoolGroupsOptions()

  const images = [
    {
      original: storagePath + '/demo-slide/elev-bokhylla.jpg',
      description: <p>När eleven loggar in på sin Doklet ser hen sina böcker och vilken lärare som har delat boken. En elev kan <i>inte</i> se böckers årskurs och svårighetsnivå. En elev kan heller inte se vilken eller vilka eventuella grupper hen tillhör.</p>,
    },
    {
      original: storagePath + '/demo-slide/elev-bok.jpg',
      description: <p>När eleven har öppnat en bok visas bokens avsnitt i vänsterkanten. Eleven kan klicka in sig på aktuellt avsnitt för att läsa och göra övningar.</p>,
    },
  ]

  useEffect(() => {
    if (demoSchoolGroups) {
      setSchoolGroup(demoSchoolGroups.find(sg => sg.id === id))
    }
  }, [demoSchoolGroups, id])

  return (
    <>
      <SubHeader breadcrumb={breadcrumb} >
        <SubHeader.MiddleSection />

        <SubHeader.RightSection>
          {schoolGroup && schoolGroupsOptions.length > 0 && (
            <SelectButton
              onChange={(e) => {
                navigate(e.target.value)
              }}
              name={'schoolGroup'}
              id={'schoolGroup'}
              options={schoolGroupsOptions}
              defaultValue={`/demogrupp/${schoolGroup.id}`}
              extraClasses={'max-w-[200px]'}
            />
          )}
        </SubHeader.RightSection>
      </SubHeader>

      {!schoolGroup && !error && <Loader />}
      {error && <Text>Någonting gick fel när vi skulle hämta grupp</Text>}

      {schoolGroup && !error && (
        <div
          className={'flex flex-col'}
          style={{ minHeight: 'calc(100vh - 146px)' }}
        >
          <div
            className={
              'absolute bg-demoGreen w-min pr-16 pl-4 py-2 right-0 top-[178px]'
            }
          >
            Demo
          </div>

          <Container size={CONTAINER_STYLE.DESKTOP}>
            <div className={'flex justify-center'}>
              <Heading
                headingLevel={HEADING_TYPE.H1}
                size={TEXT_SIZES.XXLARGE}
                color={COLORS.DARK_DEMO_GREEN.TEXT}
              >
                {schoolGroup?.name}
              </Heading>
            </div>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <div className={'mx-auto max-w-[700px]'}>
              <Text>
                Eleverna i en klass kan delas in i nivågrupper. Då kan elever i samma klass få böcker med olika svårighetsnivå.
                <br />
                <br />
                I denna demogrupp kan du lägga till/ta bort elever som finns i demoklassen. Prova i lugn och ro, utan att behöva involvera dina egna elever.
                <br />
              </Text>
            </div>

            <Divider size={DIVIDER_SIZE.BASE} />

            <div className={'flex items-center justify-between'}>
              <Button
                type={BUTTON_TYPE.SECONDARY}
                size={BUTTON_SIZE.MEDIUM}
                icon={'PlusCircle'}
                iconLeft
                margin
                handleClick={() => {
                  dispatch(
                    openPanelWithData({
                      panel: PANELS.ADD_STUDENT_TO_DEMO_SCHOOL_GROUP,
                      data: {
                        schoolId: schoolGroup?.school?.id,
                        schoolGroupId: schoolGroup?.id,
                        isDemo: true,
                      },
                    })
                  )
                }}
              >
                Lägg till elever
              </Button>
              <Button
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.MEDIUM}
                margin
                handleClick={() => {
                  setShowGallery(true)
                }}
              >
                Visa hur Doklet ser ut för en elev
              </Button>
            </div>
          </Container>

          <Divider line size={DIVIDER_SIZE.SMALL} />

          <div className={'xl:container xl:mx-auto px-10 flex-auto h-[1px]'}>
            <GridColumns cols={4} classes={'h-full gap-7'}>
              <div className={'h-full border-r border-mediumGrey'}>
                <Divider size={DIVIDER_SIZE.BASE} />
                <StudentList
                  items={schoolGroup.students}
                  resourceId={schoolGroup.id}
                  updateHook={useUpdateDemoSchoolGroup}
                />
                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
              <div className={'col-span-3'}>
                <Divider size={DIVIDER_SIZE.BASE} />

                <SharedBooksList
                  items={schoolGroup.books}
                  resourceType={SCHOOL_RESOURCE_TYPES.DEMO_GROUP}
                  resource={schoolGroup}
                />

                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
            </GridColumns>
          </div>
        </div>
      )}

      <ImageGallery
        images={images}
        onClose={() => {
          setShowGallery(false)
        }}
        show={showGallery}
      />
    </>
  )
}

export default SingleDemoSchoolGroup
